/* eslint-disable no-plusplus */
import _, { chain } from 'underscore';
import $ from 'jquery';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import * as i18nErrors from '@bingads-webui-component-legacy/api-errors-campaign/index';
import { stringifySync } from '@bingads-webui/safe-json';
import timeZoneMapper from '@bingads-webui-component-legacy/timezonemapper/index';
import * as scrollUtils from '@bingads-webui-component-legacy/extension/scrollTo';

import {
  deviceDefaultSetting,
  advancedBookingDefaultSetting,
  checkinDayDefaultSetting,
  userSetDatesDefaultSetting,
  checkinDateDefaultSetting,
  lengthOfStayDefaultSetting,
} from '@bingads-webui-campaign/hotel-common-targets';
import { HotelErrorToErrorMessageMapping } from './components/override-error';
import { audienceAdsOptionKeys } from './components/audience-ads/utility';


export function collectCriterions(feature, unSupportedFeatures, criterions) {
  if (unSupportedFeatures.includes(feature)) {
    return [];
  }
  return criterions;
}

export const countNumberOfDigits = (num) => {
  const number = num.toString().split('.')[1];
  return _.isUndefined(number) ? 0 : number.length;
};

export const getAccountCurrencyCode = serverContext => (serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.CurrencyCode) || 'USD';

export const numberErrorCode = {
  RequiredField: 'RequiredField',
  InValidNumber: 'InValidNumber',
  OutOfRange: 'OutOfRange',
  OutOfRangeNotIncludeMin: 'OutOfRangeNotIncludeMin',
  NotPositive: 'NotPositive',
  MaxLength: 'MaxLength',
  WholeNumber: 'WholeNumber',
};

export const getNumberErrorMsg = ({
  code, i18n, min, max,
}) => {
  switch (code) {
    case numberErrorCode.RequiredField:
      return i18n.getString(_TL_('Required field'));
    case numberErrorCode.InValidNumber:
      return i18n.getString(_TL_('Field requires numeric value'));
    case numberErrorCode.WholeNumber:
      return i18n.getString(_TL_('Number should be a whole number'));
    case numberErrorCode.OutOfRange:
      return i18n.getString(_TL_('Number should be between {{min}} and {{max}}'), { min: min && decimal.fromFloatToStringNoTrailingZero(min), max: max && decimal.fromFloatToStringNoTrailingZero(max) });
    case numberErrorCode.OutOfRangeNotIncludeMin:
      return i18n.getString(_TL_('Number should be larger than {{min}} and no more than {{max}}'), { min: min && decimal.fromFloatToStringNoTrailingZero(min), max: max && decimal.fromFloatToStringNoTrailingZero(max) });
    case numberErrorCode.MaxLength:
      return i18n.getString(_TL_('Number should not exceed max length'));
    default:
      return null;
  }
};

export const validateNumber = ({
  value,
  min,
  max,
  notRequired,
  shouldBePositive,
  isInteger,
  digitLength,
  notIncludeMin,
}) => {
  const isValidNumber = typeof value === 'number' ? true : decimal.isValid(value);

  if (value === '' || value == null) {
    if (notRequired) {
      return null;
    }
    return numberErrorCode.RequiredField;
  } else if (!isValidNumber) {
    return numberErrorCode.InValidNumber;
  }

  if (isInteger && (Number(value) !== parseInt(value, 10))) {
    return numberErrorCode.WholeNumber;
  }

  if (digitLength && countNumberOfDigits(value) > digitLength) {
    return numberErrorCode.MaxLength;
  }

  const number = decimal.fromLocalToFloat(value);
  if (shouldBePositive) {
    if (number < 0) {
      return numberErrorCode.NotPositive;
    }
  }

  if (notIncludeMin) {
    if (number <= min || number > max) {
      return numberErrorCode.OutOfRangeNotIncludeMin;
    }
  }

  if (number < min || number > max) {
    return numberErrorCode.OutOfRange;
  }

  return null;
};

export const bidStrategyOptionKeys = {
  ManualCpc: 'ManualCpc',
  PercentCpc: 'PercentCpc',
  Commission: 'Commission',
};

const bidConst = {
  min: 0.05,
  max: 1000,
};
export const getBidErrorMsg = ({
  code, i18n, min = bidConst.min, max = bidConst.max, isPercent,
}) => {
  switch (code) {
    case numberErrorCode.RequiredField:
      if (isPercent) {
        return i18n.getString(_TL_('Enter a percent'));
      }
      return i18n.getString(_TL_('Enter a bid'));
    default:
      return getNumberErrorMsg({
        code, i18n, min, max,
      });
  }
};

export function convertODataObjectToCampaignState(ODataObject) {
  const data = {
    Id: ODataObject.Id,
    name: {
      value: ODataObject.Name,
    },
    audienceAds: {
      currentLevelSettings: {
        option: ODataObject.NativeBidAdjustment == null || ODataObject.NativeBidAdjustment >= 0 ? audienceAdsOptionKeys.Increase : audienceAdsOptionKeys.Decrease,
        value: ODataObject.NativeBidAdjustment == null ? 0 : Math.abs(ODataObject.NativeBidAdjustment),
      },
      parentLevelSettings: {
      },
      useParentLevel: false,
    },
    budget: {
      option: 'Individual',
      individualOption: 'true',
      amount: ODataObject.Budget && ODataObject.Budget.Amount ? decimal.fromFloatToStringNoTrailingZero(ODataObject.Budget.Amount) : 0,
    },
    bidStrategy: {
      option: ODataObject.BiddingScheme ? ODataObject.BiddingScheme.Type : 'ManualCpc',
      PercentCpc: ODataObject.BiddingScheme && ODataObject.BiddingScheme.MaxPercentCpc && decimal.fromFloatToStringNoTrailingZero(ODataObject.BiddingScheme.MaxPercentCpc),
      Commission: ODataObject.BiddingScheme && ODataObject.BiddingScheme.CommissionRate && decimal.fromFloatToStringNoTrailingZero(ODataObject.BiddingScheme.CommissionRate),
    },
    urlOptions: {
      TrackingUrlTemplate: ODataObject.TrackingUrlTemplate,
      FinalUrlSuffix: ODataObject.FinalUrlSuffix,
      UrlCustomParameters: ODataObject.UrlCustomParameters,
    },
    TimeZone: timeZoneMapper.getAccountTimeZone(),
    AdScheduleUseSearcherTimeZone: ODataObject.AdScheduleUseSearcherTimeZone,
  };
  return data;
}

function commonConverODataObjectToTargetsState({
  ODataObject, criterionType, keyCb, key, defaultSetting,
}) {
  const commonTargetCriterion = {};
  chain(ODataObject)
    .filter(item => item && item.Criterion &&
      item.Criterion['@odata.type'] === criterionType && item.CriterionBid)
    .value()
    .forEach((item) => {
      commonTargetCriterion[keyCb(item)] = {
        Id: item.Id,
        bidAdjustmentDropdown: `${item.CriterionBid.Multiplier < 0 ? 'Decrease by' : 'Increase by'}`,
        bidAdjustmentInput: item.CriterionBid.Multiplier < 0 ? -(item.CriterionBid.Multiplier - 0) : item.CriterionBid.Multiplier,
      };
    });

  // by default settings sort
  const commonSettings = chain(defaultSetting)
    .map(item => ({
      ...item,
      Id: commonTargetCriterion[item[key]] && commonTargetCriterion[item[key]].Id,
      bidAdjustmentDropdown: commonTargetCriterion[item[key]] ? commonTargetCriterion[item[key]].bidAdjustmentDropdown : 'Increase by',
      bidAdjustmentInput: commonTargetCriterion[item[key]] ? commonTargetCriterion[item[key]].bidAdjustmentInput : 0,
    })).value();
  return commonSettings;
}

function commonConverODataObjectToRangeTargetsState({
  ODataObject, criterionType, key1, key2, MTKey1, MTKey2,
}) {
  const commonTargetCriterion = [];
  chain(ODataObject)
    .filter(item => item && item.Criterion &&
      item.Criterion['@odata.type'] === criterionType && item.CriterionBid)
    .value()
    .forEach((item) => {
      commonTargetCriterion.push({
        [key1]: item.Criterion[MTKey1],
        [key2]: item.Criterion[MTKey2],
        bidAdjustmentDropdown: `${item.CriterionBid.Multiplier < 0 ? 'Decrease by' : 'Increase by'}`,
        bidAdjustmentInput: item.CriterionBid.Multiplier < 0 ? -(item.CriterionBid.Multiplier - 0) : item.CriterionBid.Multiplier,
        Id: item.Id,
        Status: item.Status,
      });
    });
  return commonTargetCriterion;
}

export function converODataObjectToDeviceState(ODataObject) {
  const DeviceMap = {
    Tablets: _TL_('Tablet'),
    Computers: _TL_('Computer'),
    Smartphones: _TL_('Smartphone'),
  };
  return commonConverODataObjectToTargetsState({
    ODataObject, criterionType: '#Model.DeviceCriterion', keyCb(item) { return DeviceMap[item.Criterion.DeviceName]; }, key: 'device', defaultSetting: deviceDefaultSetting,
  });
}

export function converODataObjectToABWState(ODataObject) {
  return commonConverODataObjectToRangeTargetsState({
    ODataObject, criterionType: '#Model.AdvanceBookingWindowCriterion', key1: 'minimumDays', key2: 'maximumDays', MTKey1: 'MinDays', MTKey2: 'MaxDays',
  });
}

export function converODataObjectToLengthOfStayState(ODataObject) {
  return commonConverODataObjectToRangeTargetsState({
    ODataObject, criterionType: '#Model.LengthOfStayCriterion', key1: 'minimumNumber', key2: 'maximumNumber', MTKey1: 'MinNights', MTKey2: 'MaxNights',
  });
}

export function converODataObjectToCheckinDayState(ODataObject) {
  return commonConverODataObjectToTargetsState({
    ODataObject, criterionType: '#Model.CheckInDayCriterion', keyCb(item) { return item.Criterion.CheckInDay; }, key: 'day', defaultSetting: checkinDayDefaultSetting,
  });
}

export function converODataObjectToCheckinDateState(ODataObject) {
  const criterionType = '#Model.CheckInDateCriterion';
  const key1 = 'startDate';
  const key2 = 'endDate';
  const MTKey1 = 'StartDate';
  const MTKey2 = 'EndDate';

  const commonTargetCriterion = [];
  chain(ODataObject)
    .filter(item => item && item.Criterion &&
      item.Criterion['@odata.type'] === criterionType && item.CriterionBid)
    .value()
    .forEach((item) => {
      commonTargetCriterion.push({
        [key1]: new Date(item.Criterion[MTKey1]),
        [key2]: new Date(item.Criterion[MTKey2]),
        bidAdjustmentDropdown: `${item.CriterionBid.Multiplier < 0 ? 'Decrease by' : 'Increase by'}`,
        bidAdjustmentInput: item.CriterionBid.Multiplier < 0 ? -(item.CriterionBid.Multiplier - 0) : item.CriterionBid.Multiplier,
        Id: item.Id,
        Status: item.Status,
      });
    });
  return commonTargetCriterion;
}

export function converODataObjectToUserSetDatesState(ODataObject) {
  const UserSetDatesMap = {
    UserSelection: 'Selected date',
    DefaultSelection: 'Default date',
  };
  return commonConverODataObjectToTargetsState({
    ODataObject, criterionType: '#Model.DateSelectionTypeCriterion', keyCb(item) { return UserSetDatesMap[item.Criterion.HotelDateSelectionType]; }, key: 'dates', defaultSetting: userSetDatesDefaultSetting,
  });
}

export function send(activity, message, others = {}) {
  activity.signal(stringifySync({ messageType: message, ...others }));
}

export function sendErrors(activity, message, response) {
  try {
    const { errors } = response;
    const errorCodes = _.chain(errors).map(error => error.Error || error.Code).uniq().value();
    errorCodes.forEach((error) => {
      send(activity, message, { error });
    });
  // eslint-disable-next-line no-empty
  } catch (err) {
  }
}

export function createDefaultHotelGroup(cid, aid, campaignId, adGroupId, type, bid) {
  let defaultHotelGroup = {
    Id: -1,
    ParentId: null,
    RateBidAmount: null,
    IsLeaf: true,
    IsExcluded: false,
    IsBidLandscapeEnable: false,
    Condition: {
      Operand: 'All',
      Attribute: null,
    },
    CampaignId: parseInt(campaignId, 10),
    AdGroupId: parseInt(adGroupId, 10),
  };

  if (type === 'FixedBid') {
    defaultHotelGroup = {
      ...defaultHotelGroup,
      BidAmount: {
        Bid: {
          Amount: bid && decimal.fromLocalToFloat(bid),
        },
      },
    };
  } else {
    defaultHotelGroup = {
      ...defaultHotelGroup,
      RateBidAmount: {
        RateAmount: {
          Amount: bid && decimal.fromLocalToFloat(bid),
        },
      },
    };
  }

  const defaultHotelGroupPayload = {
    HotelListingGroupsToAdd: [
      defaultHotelGroup,
    ],
    HotelListingGroupsToDelete: [],
  };

  return edmCampaign.Customers.$withKey(cid).Accounts
    .$withKey(aid).Campaigns.$withKey(campaignId).AdGroups.$withKey(adGroupId).HotelListingGroups['Default.UpdateHotelListingTree']
    .$makeCall({ functionParams: {}, payload: defaultHotelGroupPayload }).toPromise();
}

export function getCampaignData(cid, aid, campaignId) {
  return new Promise(resolve =>
    edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Campaigns.$withKey(campaignId).get({
      select: ['Budget'],
    }).toPromise()
      .then((response) => {
        resolve(convertODataObjectToCampaignState(response));
      })
      .catch((error) => {
        console.log(error);
        resolve({});
      }));
}

export function parseDayTimeRanges(targets, resetId = false) {
  const schedulingRows = [];

  targets.forEach((target) => {
    const criterion = target.Criterion;
    const multiplier = target.CriterionBid.Multiplier;

    schedulingRows.push({
      Id: resetId ? '' : target.Id,
      Day: criterion.Day,
      StartHour: criterion.FromHour,
      StartMinute: criterion.FromMinute,
      EndHour: criterion.ToHour,
      EndMinute: criterion.ToMinute,
      BidAdjustmentType: multiplier < 0 ? -1 : 1,
      BidAdjustmentAmount: window.Math.abs(multiplier),
    });
  });

  return schedulingRows;
}

export function getAdGroupTargetsCriterions({
  type, cid, aid, campaignId, adGroupId,
}) {
  return new Promise(resolve =>
    edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Campaigns.$withKey(campaignId).AdGroups.$withKey(adGroupId).AdGroupCriterions.$cast('Model.AdGroupCriterion').get({
      filter: {
        where: type,
      },
    }).toPromise()
      .then((response) => {
        resolve(response.value);
      })
      .catch((error) => {
        console.log(error);
        resolve([]);
      }));
}

export function getCampaignTargetsCriterions({
  type, cid, aid, campaignId,
}) {
  return new Promise(resolve =>
    edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Campaigns.$withKey(campaignId).CampaignCriterions.$cast('Model.CampaignCriterion').get({
      filter: {
        where: type,
      },
    }).toPromise()
      .then((response) => {
        resolve(response.value);
      })
      .catch((error) => {
        console.log(error);
        resolve([]);
      }));
}

function analyzeHotelSettings(ODataObject) {
  const { Settings } = ODataObject;
  if (Settings) {
    const HotelSeting = Settings.find(x => x['@odata.type'] === '#Model.HotelSetting');
    if (HotelSeting) {
      const HotelAdGroupType = HotelSeting.HotelAdGroupType || '';
      return ({
        adFormat: {
          HPA: HotelAdGroupType.includes('HotelAd'),
          PPA: HotelAdGroupType.includes('PropertyAd'),
        },
      });
    }
  }
  return ({
    adFormat: {
      HPA: false,
      PPA: false,
    },
  });
}

function getBaseBid(ODataObject) {
  const data = {
  };
  if (ODataObject.PercentCpcBid) {
    data.percentCpcBid = ODataObject.PercentCpcBid && ODataObject.PercentCpcBid.RateAmount && ODataObject.PercentCpcBid.RateAmount.Amount;
    data.percentCpcBid = data.percentCpcBid && decimal.fromFloatToStringNoTrailingZero(data.percentCpcBid);
  }
  if (ODataObject.CommissionRate) {
    data.commissionRate = ODataObject.CommissionRate && ODataObject.CommissionRate.RateAmount && ODataObject.CommissionRate.RateAmount.Amount;
    data.commissionRate = data.commissionRate && decimal.fromFloatToStringNoTrailingZero(data.commissionRate);
  }
  data.searchBid = ODataObject.CpcBid && decimal.fromFloatToStringNoTrailingZero(ODataObject.CpcBid);
  return data;
}

export function convertODataObjectToAdGroupState(ODataObject) {
  return {
    TimeZone: timeZoneMapper.getAccountTimeZone(),
    Id: ODataObject.Id,
    name: {
      value: ODataObject.Name,
    },
    audienceAds: {
      currentLevelSettings: {
        option: ODataObject.NativeBidAdjustment == null || ODataObject.NativeBidAdjustment >= 0 ? audienceAdsOptionKeys.Increase : audienceAdsOptionKeys.Decrease,
        value: ODataObject.NativeBidAdjustment == null ? 0 : Math.abs(ODataObject.NativeBidAdjustment),
      },
      parentLevelSettings: {
      },
      useParentLevel: ODataObject.NativeBidAdjustment == null,
    },
    StartDate: ODataObject.StartDate,
    EndDate: ODataObject.EndDate,
    Status: ODataObject.Status,
    AdDistribution: ODataObject.AdDistribution,
    AdScheduleUseSearcherTimeZone: ODataObject.AdScheduleUseSearcherTimeZone,
    baseBid: getBaseBid(ODataObject),
    ...analyzeHotelSettings(ODataObject),
    urlOptions: {
      TrackingUrlTemplate: ODataObject.TrackingUrlTemplate,
      FinalUrlSuffix: ODataObject.FinalUrlSuffix,
      UrlCustomParameters: ODataObject.UrlCustomParameters,
    },
  };
}

export function getAdGroupData(cid, aid, campaignId, adGroupId) {
  return new Promise(resolve =>
    edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Campaigns.$withKey(campaignId).AdGroups.$withKey(adGroupId).get({
    }).toPromise()
      .then((response) => {
        resolve(convertODataObjectToAdGroupState(response));
      })
      .catch((error) => {
        console.log(error);
        resolve({});
      }));
}

function getTargetCriterionType(type) {
  if (type.indexOf('DeviceOS Criterion') > -1) {
    return 'device';
  }
  if (type.indexOf('Advance Booking Window Criterion') > -1) {
    return 'ABW';
  }
  if (type.indexOf('Length of Stay Criterion') > -1) {
    return 'lengthOfStay';
  }
  if (type.indexOf('Check In Day Criterion') > -1) {
    return 'checkinDay';
  }
  if (type.indexOf('Check In Date Criterion') > -1) {
    return 'checkinDate';
  }
  if (type.indexOf('Hotel Date Selection Type Criterion') > -1) {
    return 'userSetDates';
  }
  if (type.indexOf('Location Intent Criterion') > -1 || type.indexOf('Location Criterion') > -1) {
    return 'location';
  }
  return null;
}

export function updateDeviceByResponse(criterion, device, patchCampaignOrAdgroup) {
  const DeviceMap = {
    Tablets: 'Tablet',
    Computers: 'Computer',
    Smartphones: 'Smartphone',
  };
  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        targeted: 'Targeted',
        device: DeviceMap[currentCriterion.Criterion.DeviceCriterion.DeviceName],
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = deviceDefaultSetting;
  }
  const tempDevice = {
    ...device,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('device', tempDevice);
  }
  return tempCurrentLevelSettings;
}

export function updateABWByResponse(criterion, ABW, patchCampaignOrAdgroup) {
  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        minimumDays: currentCriterion.Criterion.AdvanceBookingWindowCriterion.MinDays,
        maximumDays: currentCriterion.Criterion.AdvanceBookingWindowCriterion.MaxDays,
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = advancedBookingDefaultSetting;
  }
  const tempABW = {
    ...ABW,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('ABW', tempABW);
  }
  return tempCurrentLevelSettings;
}

export function updateCheckinDayByResponse(criterion, checkinDay, patchCampaignOrAdgroup) {
  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        day: currentCriterion.Criterion.CheckInDayCriterion.CheckInDay,
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = checkinDayDefaultSetting;
  }
  const tempCheckinDay = {
    ...checkinDay,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('checkinDay', tempCheckinDay);
  }
  return tempCurrentLevelSettings;
}

export function updateLengthOfStayByResponse(criterion, lengthOfStay, patchCampaignOrAdgroup) {
  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        minimumNumber: currentCriterion.Criterion.LengthofStayCriterion.MinNights,
        maximumNumber: currentCriterion.Criterion.LengthofStayCriterion.MaxNights,
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = lengthOfStayDefaultSetting;
  }
  const tempLengthOfStay = {
    ...lengthOfStay,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('lengthOfStay', tempLengthOfStay);
  }
  return tempCurrentLevelSettings;
}

export function updateCheckinDateByResponse(criterion, checkinDate, patchCampaignOrAdgroup) {
  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        startDate: new Date(currentCriterion.Criterion.CheckInDateCriterion.StartDate),
        endDate: new Date(currentCriterion.Criterion.CheckInDateCriterion.EndDate),
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = checkinDateDefaultSetting;
  }
  const tempCheckinDate = {
    ...checkinDate,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('checkinDate', tempCheckinDate);
  }
  return tempCurrentLevelSettings;
}

export function updateUserSetDatesByResponse(criterion, userSetDates, patchCampaignOrAdgroup) {
  const UserSetDatesMap = {
    UserSelection: 'Selected date',
    DefaultSelection: 'Default date',
  };

  let tempCurrentLevelSettings = [];
  for (let j = 0; j < criterion.length; j++) {
    const currentCriterion = criterion[j];
    if (currentCriterion.Status !== 'Deleted') {
      tempCurrentLevelSettings.push({
        dates: UserSetDatesMap[currentCriterion.Criterion.DateSelectionTypeCriterion.HotelDateSelectionType],
        bidAdjustmentDropdown: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? 'Increase by' : 'Decrease by',
        bidAdjustmentInput: currentCriterion.Criterion.BidAdjustment - 0 >= 0 ? currentCriterion.Criterion.BidAdjustment - 0 : -currentCriterion.Criterion.BidAdjustment,
        Id: currentCriterion.Id,
        Status: currentCriterion.Status,
      });
    }
  }
  if (tempCurrentLevelSettings.length === 0) {
    tempCurrentLevelSettings = userSetDatesDefaultSetting;
  }
  const tempUserSetDates = {
    ...userSetDates,
    currentLevelSettings: tempCurrentLevelSettings,
    reset: true,
  };
  if (patchCampaignOrAdgroup) {
    patchCampaignOrAdgroup('userSetDates', tempUserSetDates);
  }
  return tempCurrentLevelSettings;
}

function expandSettingsWithErrors(response, subViews) {
  const properties = _.groupBy(response.entity.errors, 'property');
  const urlOptionsHaveErrors = _.has(properties, 'TrackingUrlTemplate') || _.has(properties, 'UrlCustomParameters') || _.has(properties, 'FinalUrlSuffix');
  if (urlOptionsHaveErrors) {
    subViews.urlOptionsView.expandSettingsWithApiErrors(_.has(properties, 'TrackingUrlTemplate'), _.has(properties, 'UrlCustomParameters'), _.has(properties, 'FinalUrlSuffix'));
  }
}

function errorElements(level) {
  return $(`.hotel-${level}-shared .error-string,.hotel-${level}-shared .error, .hotel-${level}-shared .alert-danger`).filter(':visible');
}

export function scrollToErrors(level) {
  setTimeout(() => { scrollUtils.scrollAndCenter(errorElements(level)); }, 0);
}

export function showWarning(level, i18n, removeLastWarning) {
  if (removeLastWarning) {
    removeLastWarning();
  }
  const id = notificationApi.showWarning(i18n.getString(_TL_('Please correct the following errors.')));
  const interval = {};
  const timeout = 500;
  const removeWarningInterval = () => {
    if (interval.id) {
      notificationApi.dismiss(id);
      clearInterval(interval.id);
      interval.id = undefined;
    }
  };
  const dismissFunc = () => {
    // clear the error automatically,
    // TODO: check the error from react component state or via jquery DOM?
    if (errorElements(level).length === 0) {
      removeWarningInterval();
    }
  };
  interval.id = setInterval(dismissFunc, timeout);
  return removeWarningInterval;
}

function handleCommonTargetsError(entity, i18n, errors) {
  const allTargetsApiErrors = {};
  for (let i = 0; i < errors.length; i++) {
    const currentError = errors[i];
    for (let j = 0; j < entity.length; j++) {
      const currentEntity = entity[j];
      if (currentError['Client Id'] === currentEntity['Client Id']) {
        if (!allTargetsApiErrors[getTargetCriterionType(currentError.Type)]) {
          allTargetsApiErrors[getTargetCriterionType(currentError.Type)] = [];
        }
        const key = HotelErrorToErrorMessageMapping[currentEntity.errors[0].code];
        const newHotelErrMsg = key ? i18n.getString(key) : undefined;
        const apiErrMsg = newHotelErrMsg || currentEntity.errors[0].message;
        if (allTargetsApiErrors[getTargetCriterionType(currentError.Type)].indexOf(apiErrMsg) === -1) {
          allTargetsApiErrors[getTargetCriterionType(currentError.Type)].push(apiErrMsg);
        }
        break;
      }
    }
  }
  return allTargetsApiErrors;
}

function parseEntityApiErrors(response, level) {
  let errors = (response.entity && response.entity.errors) || [];

  if (level === 'campaign' && response.entity && response.entity.Id) {
    let adGroupErrors = [];
    // Collect ad group errors
    if (response.errors) {
      adGroupErrors = i18nErrors.getBulkApiErrors(response.errors.filter(error => error.Type === 'AdGroup Error')).map(error => ({ ...error, property: 'General' }));
    }
    errors = errors.concat(adGroupErrors);
  }

  if (errors && errors.length) {
    const general = 'General';
    const ignoredErrors = ['TrackingUrlTemplate', 'UrlCustomParameters', 'FinalUrlSuffix'];
    const classified = ['Name'];
    errors = errors.filter(error => !ignoredErrors.includes(error.property));
    errors.forEach((error) => {
      const target = error;
      if (_.isEmpty(target.property) || !classified.includes(target.property)) {
        target.property = general;
      }
    });
    return _.groupBy(errors, 'property');
  }
  return {};
}

export function mergeAdGroupStateWithErrors(state, errors) {
  const {
    entityApiErrors,
    targetsApiErrors,
  } = errors;
  return {
    ...state,
    name: {
      ...state.name,
      errors: entityApiErrors.Name ? [...entityApiErrors.Name] : [],
    },
    location: {
      ...state.location,
      errors: targetsApiErrors.location ? [...targetsApiErrors.location] : [],
    },
  };
}

export function mergeCampaignStateWithErrors(state, errors) {
  const {
    entityApiErrors,
    targetsApiErrors,
  } = errors;
  return {
    ...state,
    name: {
      ...state.name,
      errors: entityApiErrors.Name ? [...entityApiErrors.Name] : [],
    },
    location: {
      ...state.location,
      errors: targetsApiErrors.location ? [...targetsApiErrors.location] : [],
    },
  };
}

function handleErrors(response, i18n, subViews, criterionsType, level) {
  if (response && response.entity && !response.entity.Id) {
    // If campaign is not created, show only the entity errors,
    return {
      targetsApiErrors: {},
      advancedSettinsApiErrors: {},
      entityApiErrors: parseEntityApiErrors(response, level),
    };
  }

  _.each(subViews, (view) => {
    if (_.isFunction(view.handleErrors)) {
      response.errors = view.handleErrors(response);
    }
  });

  let urlOptionsHaveErrors = false;
  const advancedSettinsApiErrors = {};
  if (response.entity.errors) {
    const properties = _.groupBy(response.entity.errors, 'property');
    subViews.urlOptionsView.handleApiErrors(properties);
    urlOptionsHaveErrors = _.has(properties, 'TrackingUrlTemplate') || _.has(properties, 'UrlCustomParameters') || _.has(properties, 'FinalUrlSuffix');
    if (urlOptionsHaveErrors) {
      if (!advancedSettinsApiErrors.urlOptions) {
        advancedSettinsApiErrors.urlOptions = [];
      }
      if (_.has(properties, 'TrackingUrlTemplate')) { advancedSettinsApiErrors.urlOptions.push({ TrackingUrlTemplate: properties.TrackingUrlTemplate }); }
      if (_.has(properties, 'UrlCustomParameters')) { advancedSettinsApiErrors.urlOptions.push({ UrlCustomParameters: properties.UrlCustomParameters || 'ssss' }); }
      if (_.has(properties, 'FinalUrlSuffix')) { advancedSettinsApiErrors.urlOptions.push({ FinalUrlSuffix: properties.FinalUrlSuffix || 'ssssddd' }); }
    }
    response.errors = _.reject(response.errors, err => err.Property === 'TrackingUrlTemplate' || err.Property === 'UrlCustomParameters');
  }

  expandSettingsWithErrors(response, subViews);

  return {
    targetsApiErrors: handleCommonTargetsError(response.entity && response.entity[criterionsType], i18n, response.errors),
    advancedSettinsApiErrors,
    entityApiErrors: parseEntityApiErrors(response, level),
  };
}

const defaultErrorObject = {
  targetsApiErrors: {},
  advancedSettinsApiErrors: {},
  entityApiErrors: {},
};

export function handleApiErrors(response, i18n, subViews, level) {
  const criterionsType = level === 'campaign' ? 'CampaignCriterions' : 'AdGroupCriterions';

  if (_.isUndefined(response) || _.isNull(response)) {
    return defaultErrorObject;
  }

  if (response.errors && !response.entity) {
    // Ajax error [400] - RowCountExceeded
    // TODO show context error in page
    return {
      ...defaultErrorObject,
      entityApiErrors: {
        General: i18nErrors.getODataErrors(response.errors),
      },
    };
  }

  if (response === 'exclusion error') {
    return defaultErrorObject;
  }

  if (response.errors) {
    const processItems = [`${criterionsType}`].concat(Object.prototype.hasOwnProperty.call(response.entity, 'AdExtensionAssociations') ? ['AdExtensionAssociations'] : []);
    i18nErrors.getPartial(response, processItems);
    scrollToErrors(level);
    return handleErrors(response, i18n, subViews, criterionsType, level);
  }
  return defaultErrorObject;
}


export function clearSubViewApiErrors(subViews) {
  _.each(subViews, (view) => {
    if (_.isFunction(view.clearApiErrors)) {
      view.clearApiErrors();
    }
  });
}


export function invalidateCache(entityName, ids) {
  if (edmCampaign['odata.cache']) {
    edmCampaign['odata.cache'].invalidateTypeCache(entityName, null, '');
    if (!_.isEmpty(ids)) {
      _.each(ids, id => edmCampaign['odata.cache'].invalidateTypeCache(entityName, id, ''));
    }
  }
}


export const subscribeUrlOptions = (context, globalErrorMsg) => {
  context.urlOptionsView.trackingTemplate.ko_model.getTrackingTemplate.subscribe(() => {
    const urlOptionsApiErrors = context.state.advancedSettinsApiErrors.urlOptions;
    const newUrlOptionsApiErrors = [];
    if (urlOptionsApiErrors && urlOptionsApiErrors.length) {
      urlOptionsApiErrors.forEach((item) => {
        if (Object.keys(item)[0] !== 'TrackingUrlTemplate') { newUrlOptionsApiErrors.push(item); }
      });
      context.urlOptionsView.trackingTemplate.ko_model.trackingTemplateErrors([]);
      context.patchAdvancedSettinsApiErrors('urlOptions', newUrlOptionsApiErrors);
    }
    if (!newUrlOptionsApiErrors.length && context.urlOptionsView.isValid()) {
      context.patchErrMsg('urlOptions', '');
    } else {
      context.patchErrMsg('urlOptions', globalErrorMsg);
    }
  });
  context.urlOptionsView.finalUrlSuffix.viewModel.errorMessages.subscribe(() => {
    const urlOptionsApiErrors = context.state.advancedSettinsApiErrors.urlOptions;
    const newUrlOptionsApiErrors = [];
    if (urlOptionsApiErrors && urlOptionsApiErrors.length) {
      urlOptionsApiErrors.forEach((item) => {
        if (Object.keys(item)[0] !== 'FinalUrlSuffix') { newUrlOptionsApiErrors.push(item); }
      });
      if (context.urlOptionsView.options.isFinalURLSuffixEnabled) {
        context.urlOptionsView.finalUrlSuffix.viewModel.externalErrors([]);
      }
      context.patchAdvancedSettinsApiErrors('urlOptions', newUrlOptionsApiErrors);
    }
    if (!newUrlOptionsApiErrors.length && context.urlOptionsView.isValid()) {
      context.patchErrMsg('urlOptions', '');
    } else {
      context.patchErrMsg('urlOptions', globalErrorMsg);
    }
  });
  context.urlOptionsView.customParams.viewModel.parameterArray.subscribe(() => {
    const urlOptionsApiErrors = context.state.advancedSettinsApiErrors.urlOptions;
    const newUrlOptionsApiErrors = [];
    if (urlOptionsApiErrors && urlOptionsApiErrors.length) {
      urlOptionsApiErrors.forEach((item) => {
        if (Object.keys(item)[0] !== 'UrlCustomParameters') { newUrlOptionsApiErrors.push(item); }
      });
      context.urlOptionsView.customParams.viewModel.parametersError([]);
      context.patchAdvancedSettinsApiErrors('urlOptions', newUrlOptionsApiErrors);
    }
    if (!newUrlOptionsApiErrors.length && context.urlOptionsView.isValid()) {
      context.patchErrMsg('urlOptions', '');
    } else {
      context.patchErrMsg('urlOptions', globalErrorMsg);
    }
  });
};

export const subscribeSiteExclusion = (context) => {
  context.siteExclusionView.viewModel.getExclusions.subscribe(() => {
    const baseExclusionApiErrors = context.state.advancedSettinsApiErrors.baseExclusion;
    if (baseExclusionApiErrors && baseExclusionApiErrors.length) {
      const newExclusionApiErrors = [];
      baseExclusionApiErrors.forEach((item) => {
        if (Object.keys(item)[0] !== 'siteErrors') { newExclusionApiErrors.push(item); }
      });
      context.siteExclusionView.clearApiErrors();
      context.patchAdvancedSettinsApiErrors('baseExclusion', newExclusionApiErrors);
      if (newExclusionApiErrors.length === 0) {
        context.patchErrMsg('baseExclusion', '');
      }
    }
  });
};

export const subscribeIPExclusion = (context) => {
  context.IPExclusionView.viewModel.getExclusions.subscribe(() => {
    const baseExclusionApiErrors = context.state.advancedSettinsApiErrors.baseExclusion;
    if (baseExclusionApiErrors && baseExclusionApiErrors.length) {
      const newExclusionApiErrors = [];
      baseExclusionApiErrors.forEach((item) => {
        if (Object.keys(item)[0] !== 'ipErrors') { newExclusionApiErrors.push(item); }
      });
      context.IPExclusionView.clearApiErrors();
      context.patchAdvancedSettinsApiErrors('baseExclusion', newExclusionApiErrors);
      if (newExclusionApiErrors.length === 0) {
        context.patchErrMsg('baseExclusion', '');
      }
    }
  });
};
